import React, { useEffect, useState, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Plus, Download, Maximize2, ZoomIn, Thermometer, Wind, Settings, Droplets, Gauge, Cloud, Sun, X, Check } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { ForecastSettings } from '../components/forecast/ForecastSettings';
import { cn } from '../components/ui/Button';
// Seeded random for reproducible but distinct data per parameter
function seededRandom(seed: number) {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}
function generateParamData(paramId: string, length: number) {
  const seeds: Record<string, number> = {
    temperature: 42,
    wind: 137,
    humidity: 256,
    pressure: 389,
    cloud_cover: 512,
    solar: 773
  };
  const ranges: Record<string, [number, number, number]> = {
    temperature: [12, 6, 7],
    wind: [5, 4, 5],
    humidity: [60, 20, 9],
    pressure: [1013, 8, 12],
    cloud_cover: [50, 35, 6],
    solar: [200, 150, 8]
  };
  const rand = seededRandom(seeds[paramId] || 100);
  const [base, amp, freq] = ranges[paramId] || [5, 3, 5];
  return Array.from({
    length
  }, (_, i) => ({
    time: i,
    [paramId]: +(base + Math.sin(i / freq) * amp + (rand() - 0.5) * amp * 0.6).toFixed(2)
  }));
}
function buildChartData(activeIds: string[], length: number) {
  const merged: Record<string, number | string>[] = Array.from({
    length
  }, (_, i) => ({
    time: i
  }));
  activeIds.forEach(id => {
    const paramData = generateParamData(id, length);
    paramData.forEach((point, i) => {
      merged[i][id] = point[id];
    });
  });
  return merged;
}
interface WeatherParam {
  id: string;
  label: string;
  icon: React.ReactNode;
  unit: string;
  color: string;
}
const allParameters: WeatherParam[] = [{
  id: 'temperature',
  label: 'Temperature',
  icon: <Thermometer size={14} data-id="element-2315" />,
  unit: '°C',
  color: '#f97316'
}, {
  id: 'wind',
  label: 'Wind Speed',
  icon: <Wind size={14} data-id="element-2316" />,
  unit: 'm/s',
  color: '#EC4899'
}, {
  id: 'humidity',
  label: 'Humidity',
  icon: <Droplets size={14} data-id="element-2317" />,
  unit: '%',
  color: '#3b82f6'
}, {
  id: 'pressure',
  label: 'Pressure',
  icon: <Gauge size={14} data-id="element-2318" />,
  unit: 'hPa',
  color: '#8b5cf6'
}, {
  id: 'cloud_cover',
  label: 'Cloud Cover',
  icon: <Cloud size={14} data-id="element-2319" />,
  unit: '%',
  color: '#94a3b8'
}, {
  id: 'solar',
  label: 'Solar Radiation',
  icon: <Sun size={14} data-id="element-2320" />,
  unit: 'W/m²',
  color: '#eab308'
}];
export function ForecastView() {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeParams, setActiveParams] = useState<string[]>(['temperature', 'wind']);
  const [paramPickerOpen, setParamPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setParamPickerOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const toggleParam = (id: string) => {
    setActiveParams(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };
  const activeParamObjects = allParameters.filter(p => activeParams.includes(p.id));
  const currentParam = activeParamObjects[activeParamObjects.length - 1] || allParameters[1];
  const chartData = buildChartData(activeParams, 50);
  return <div className="jua-page-bg relative h-[calc(100vh-64px)] w-full overflow-hidden" data-id="element-2321">
      {/* Main Chart Area */}
      <div className="flex-1 flex flex-col h-full" data-id="element-2322">
        {/* Top Controls */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800/50" data-id="element-2323">
          <div className="flex items-center gap-6" data-id="element-2324">
            <div className="flex items-center gap-4" data-id="element-2325">
              {['EPT-1.5', 'EPT-2', 'EPT-2e'].map((tab, i) => <button key={tab} className={cn('text-sm font-medium pb-1 border-b-2 transition-colors', i === 1 ? 'text-white border-[#EC4899]' : 'text-slate-400 border-transparent hover:text-slate-200')} data-id="element-2326">
                  {tab}
                </button>)}
              <button className="text-slate-500 hover:text-slate-300" data-id="element-2327">
                <Plus size={16} data-id="element-2328" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3" data-id="element-2329">
            <span className="text-sm text-slate-400" data-id="element-2330">Enge</span>
            <div className="flex items-center gap-1 bg-slate-800/50 rounded-lg p-1" data-id="element-2331">
              <button className="px-3 py-1 rounded-md bg-[#EC4899]/10 text-[#EC4899] text-sm font-medium" data-id="element-2332">
                London
              </button>
              <button className="p-1 text-slate-400 hover:text-white" data-id="element-2333">
                <Plus size={16} data-id="element-2334" />
              </button>
            </div>

            <Button size="icon" variant={settingsOpen ? 'primary' : 'secondary'} className="h-8 w-8 ml-1" onClick={() => setSettingsOpen(!settingsOpen)} data-id="element-2335">
              <Settings size={16} data-id="element-2336" />
            </Button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-4 flex items-center justify-between" data-id="element-2337">
          <div className="flex items-center gap-2 relative" data-id="element-2338">
            {/* Active parameter buttons */}
            {activeParamObjects.map(param => <button key={param.id} onClick={() => toggleParam(param.id)} className="group relative h-8 w-8 rounded-md flex items-center justify-center transition-colors" style={{
            backgroundColor: `${param.color}20`,
            color: param.color
          }} title={param.label} data-id="element-2339">
                {param.icon}
                <span className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-slate-900 border border-slate-700 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2340">
                  <X size={8} className="text-slate-400" data-id="element-2341" />
                </span>
              </button>)}

            {/* Add parameter button + picker */}
            <div className="relative" ref={pickerRef} data-id="element-2342">
              <Button size="icon" variant="secondary" className="h-8 w-8" onClick={() => setParamPickerOpen(!paramPickerOpen)} data-id="element-2343">
                <Plus size={16} data-id="element-2344" />
              </Button>

              {paramPickerOpen && <div className="absolute top-full left-0 mt-2 w-[260px] rounded-xl border border-slate-700/50 bg-[#0F172A] shadow-2xl shadow-black/50 z-50 overflow-hidden" data-id="element-2345">
                  <div className="px-3 py-2.5 border-b border-slate-800/60" data-id="element-2346">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-2347">
                      Weather Parameters
                    </span>
                  </div>
                  <div className="max-h-[320px] overflow-y-auto py-1" data-id="element-2348">
                    {allParameters.map(param => {
                  const isActive = activeParams.includes(param.id);
                  return <button key={param.id} onClick={() => toggleParam(param.id)} className={cn('flex items-center gap-3 w-full px-3 py-2 text-left transition-colors text-sm', isActive ? 'bg-white/[0.04]' : 'hover:bg-white/[0.03]')} data-id="element-2349">
                          <span className="h-7 w-7 rounded-md flex items-center justify-center flex-shrink-0" style={{
                      backgroundColor: `${param.color}20`,
                      color: param.color
                    }} data-id="element-2350">
                            {param.icon}
                          </span>
                          <div className="flex-1 min-w-0" data-id="element-2351">
                            <span className="text-slate-200 block" data-id="element-2352">
                              {param.label}
                            </span>
                            <span className="text-[10px] text-slate-500" data-id="element-2353">
                              {param.unit}
                            </span>
                          </div>
                          {isActive && <Check size={14} className="text-[#EC4899] flex-shrink-0" data-id="element-2354" />}
                        </button>;
                })}
                  </div>
                </div>}
            </div>
          </div>

          <div className="flex items-center gap-2" data-id="element-2355">
            <Button size="icon" variant="ghost" className="h-8 w-8" data-id="element-2356">
              <ZoomIn size={16} data-id="element-2357" />
            </Button>
            <Button size="icon" variant="ghost" className="h-8 w-8" data-id="element-2358">
              <Download size={16} data-id="element-2359" />
            </Button>
            <Button size="icon" variant="ghost" className="h-8 w-8" data-id="element-2360">
              <Maximize2 size={16} data-id="element-2361" />
            </Button>
          </div>
        </div>

        {/* Chart Header */}
        <div className="px-6 mb-4 text-center" data-id="element-2362">
          <h2 className="text-xl font-bold text-white mb-1" data-id="element-2363">London</h2>
          <div className="flex items-center justify-center gap-3 text-sm text-slate-400" data-id="element-2364">
            {activeParamObjects.map(param => <div key={param.id} className="flex items-center gap-1.5" data-id="element-2365">
                <div className="h-2 w-2 rounded-full" style={{
              backgroundColor: param.color
            }} data-id="element-2366" />
                <span data-id="element-2367">{param.label}</span>
              </div>)}
            <span className="text-slate-600" data-id="element-2368">·</span>
            <span data-id="element-2369">EPT-2</span>
          </div>
        </div>

        {/* Chart */}
        <div className="flex-1 w-full px-4 pb-8 min-h-0" data-id="element-2370">
          <ResponsiveContainer width="100%" height="100%" data-id="element-2371">
            <LineChart data={chartData} data-id="element-2372">
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} data-id="element-2373" />
              <XAxis dataKey="time" stroke="#64748b" tick={{
              fill: '#64748b',
              fontSize: 10
            }} tickLine={false} axisLine={false} data-id="element-2374" />
              <YAxis stroke="#64748b" tick={{
              fill: '#64748b',
              fontSize: 10
            }} tickLine={false} axisLine={false} label={{
              value: currentParam.unit,
              angle: -90,
              position: 'insideLeft',
              fill: '#64748b',
              fontSize: 10
            }} data-id="element-2375" />
              <Tooltip contentStyle={{
              backgroundColor: '#1e293b',
              borderColor: '#334155',
              color: '#f1f5f9',
              borderRadius: '8px',
              fontSize: '12px'
            }} labelFormatter={label => `Time: ${label}`} data-id="element-2376" />
              {activeParamObjects.map(param => <Line key={param.id} type="monotone" dataKey={param.id} name={param.label} stroke={param.color} strokeWidth={2} dot={false} activeDot={{
              r: 4,
              fill: param.color,
              stroke: '#fff'
            }} data-id="element-2377" />)}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Bottom Range Slider */}
        <div className="h-4 mx-6 mb-6 rounded-full bg-slate-800 relative overflow-hidden" data-id="element-2378">
          <div className="absolute inset-y-0 left-0 right-0 bg-gradient-to-r from-slate-800 via-[#EC4899]/20 to-slate-800" data-id="element-2379" />
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#EC4899]/50" data-id="element-2380" />
        </div>
      </div>

      {/* Sliding Settings Panel */}
      <ForecastSettings isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} data-id="element-2381" />
    </div>;
}